import { Image } from 'antd'
import { clsx } from 'clsx'
import { PreviewControl } from 'component/Image/PreviewControl'
import { fixImageUrl } from 'component/Image/util'
import { useLatestCallback } from 'hook/useLatestCallback'
import { PreviewGroupPreview } from 'rc-image/lib/PreviewGroup'
import { ReactNode, useMemo } from 'react'
import { mutable } from 'util/type'
import css from './Preview.module.scss'

type Props = {
  control: PreviewControl
  sources: readonly string[]
  open?: boolean
  strong?: boolean
  toolbar?: (source: string) => ReactNode
}

function getPreviewDefaults(): Partial<PreviewGroupPreview> {
  return {
    destroyOnClose: true,
    maxScale: 10,
    minScale: 0.1,
    scaleStep: 0.1,
  }
}

export const Preview = (props: Props) => {
  const { control, open, strong, toolbar } = props
  const sources = props.sources.map(src => fixImageUrl(src))
  const source = control.preview

  const onChange = useLatestCallback((index: number) => {
    control.setPreview(sources[index])
  })

  const onVisibleChange = useLatestCallback((value: boolean, _prev: boolean, index: number) => {
    control.setPreview(value ? sources[index] : undefined)
  })

  const openIndex = useMemo(() => {
    if (source == null) return undefined
    const found = sources.indexOf(source)
    return found < 0 ? 0 : found
  }, [source])

  const preview: PreviewGroupPreview = {
    ...getPreviewDefaults(),
    rootClassName: clsx(css.preview, open != null && css.open),
    current: openIndex,
    onChange,
    onVisibleChange,
    visible: open ?? openIndex != null,
    countRender: sources.length > 1 ? undefined : () => null,
    maskClosable: !strong,
    toolbarRender: () => source && toolbar ? <div className={css.toolbar}>{toolbar(source)}</div> : null,
    imageRender: (image) => source ? image : undefined,
  }

  return <Image.PreviewGroup preview={preview} items={mutable(sources)} />
}
